import React, { useContext } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { t } from 'i18next';
import { SiteSetupContext } from 'src/js/modules/site-setup/helpers/site-setup-context';
import routesMap from 'src/js/router/helpers/routes-map';
import config from 'src/js/modules/config';
import user from 'src/js/modules/user';
import sites from 'src/js/modules/sites';
import {
  AI_SITE_WIZARD_SITE_PROP,
  aiSiteWizardSitePropValues,
} from 'src/js/constants/ai-site-wizard';
import HowToBuildStep from '../../components/site-setup/how-to-build-step';
import useOnTemplateSelect from '../../modules/site-setup/hooks/use-on-template-select';
import getCurrentStep from '../../modules/site-setup/helpers/get-current-step';
import trackers from '../../modules/analytics/segment/trackers';
import {
  BLANK_TEMPLATE_BUILD_SLUG,
  DEFAULT_TEMPLATE_BUILD_SLUG,
} from '../../modules/templates/constants/common';
import cardTypes from '../../constants/site-setup/how-to-build-step/card-types';
import constants from '../../modules/site-setup/constants';

const {
  trackSitesetupStartWithBlankTemplateSelected,
  trackSitesetupStartWithATemplateSelected,
  trackSitesetupAiGenerateWebsiteSelected,
} = trackers;
const { triggerIds } = constants;

const getCaptions = () => ({
  title: t("Let's start building your dream website"),
  subtitle: t('How do you want to proceed?'),
  blankTemplate: {
    title: t('Know what you do?'),
    link: t('Start from scratch'),
  },
  legacyTemplate: {
    title: t('Looking for Sitebuilder Classic?'),
    link: t('Continue with classic'),
  },
});
const getCards = () => [
  {
    id: cardTypes.AI_GENERATE,
    title: t('Generate your website in seconds'),
    subtitle: t('Recommended'),
    description: t('Let AI create a website for you'),
    iconGlyph: 'ai',
    withDecoration: true,
  },
  {
    id: cardTypes.START_WITH_TEMPLATE,
    title: t('Start with a template'),
    description: t('Use designer-made templates'),
    iconGlyph: 'pages',
  },
];

const HowToBuildStepContainer = () => {
  const navigate = useNavigate();
  const context = useContext(SiteSetupContext);
  const yolacomUrl = useSelector(config.selectors.getYolacomUrl);
  const currentUserData = useSelector(user.selectors.getUserData);
  const isWhiteLabel = currentUserData ? currentUserData.isWhiteLabel : null;
  const userPreferences = useSelector(user.selectors.getUserPreferences);
  const platformAccess = userPreferences ? userPreferences.platformAccess : null;

  const siteIdRecreate = useSelector(sites.selectors.getSiteIdRecreate);
  const siteIdToSwitchTemplate = useSelector(sites.selectors.getSiteIdToSwitchTemplate);
  const onTemplateSelect = useOnTemplateSelect();

  const { getData, updateData } = context;
  const contextData = getData();
  const { number: currentStep, stepName } = getCurrentStep();
  const captions = getCaptions();
  const cards = getCards();
  const legacyPath = routesMap.legacyTemplates.url();
  const legacySitebuilderUrl = `${yolacomUrl}${legacyPath}`;
  const { businessCategorySlug, archetype = null, templates } = contextData;
  const traits = {
    stepId: stepName,
    businessCategory: businessCategorySlug,
    archetypeId: archetype || null,
  };

  const b2cFullAccess =
    Boolean(currentUserData) &&
    !isWhiteLabel &&
    platformAccess === user.platformAccess.ALL &&
    !siteIdRecreate &&
    !siteIdToSwitchTemplate;

  const onBlankTemplateClick = (e) => {
    e.preventDefault();

    trackSitesetupStartWithBlankTemplateSelected(traits);

    updateData({ [AI_SITE_WIZARD_SITE_PROP]: aiSiteWizardSitePropValues.SKIPPED });
    onTemplateSelect(BLANK_TEMPLATE_BUILD_SLUG, triggerIds.BLANK_TEMPLATE);
  };

  const onCardClick = (id) => {
    if (id === cardTypes.AI_GENERATE) {
      trackSitesetupAiGenerateWebsiteSelected(traits);

      let templateSlug;

      if (templates.length) {
        const randomIndex = Math.floor(Math.random() * templates.length);
        templateSlug = templates[randomIndex];
      } else {
        templateSlug = DEFAULT_TEMPLATE_BUILD_SLUG;
      }

      updateData({ [AI_SITE_WIZARD_SITE_PROP]: aiSiteWizardSitePropValues.INITIATED });
      onTemplateSelect(templateSlug, triggerIds.AI_SITE);
      return;
    }

    if (id === cardTypes.START_WITH_TEMPLATE) {
      trackSitesetupStartWithATemplateSelected(traits);
      updateData({ [AI_SITE_WIZARD_SITE_PROP]: aiSiteWizardSitePropValues.ENABLED });
      navigate(routesMap.gallery.url());
    }
  };

  return (
    <HowToBuildStep
      captions={captions}
      currentStep={currentStep}
      cards={cards}
      isLegacySitebuilderAvailable={b2cFullAccess}
      legacySitebuilderUrl={legacySitebuilderUrl}
      onCardClick={onCardClick}
      onBlankTemplateClick={onBlankTemplateClick}
    />
  );
};

export default HowToBuildStepContainer;
